import React, { Component } from 'react';
import PropTypes from 'prop-types';
import ScoreBarTypes from './../../constants/ScoreBarTypes';
import Normal from './components/Normal/Normal';
import Stars from './components/Stars/Stars';
import Croissant from './components/Croissant/Croissant';
import BarGraph from './components/BarGraph/BarGraph';
import CutOff from './components/CutOff/CutOff';
import { Gauge } from './components/Gauge/Gauge';
import ScoreWithDevelopability from './components/ScoreWithDevelopability/ScoreWithDevelopability';
import DisplayScore from './../DisplayScore/DisplayScore';

export default class ScoreBarNG extends Component {
    render() {
        const {
            classes,
            scoreMin,
            scoreMax,
            scoreValue,
            scoreValueVisible,
            displayScore,
            scoreBarConfig,
            scoreName,
            scoreBarType,
            languageId,
            developabilityLabel,
            developabilityScore,
            developabilityDefinition,
            scoreDivisionMinimum,
            scoreLevelDescription,
            evaluation,
            label,
            autoScaleWidth,
            disabled,
            digits,
            floor,
            valueLabelDisplay,
            defaultValue,
            onChange,
            onChangeCommitted,
            cutOff,
            initials,
            levelRanges
        } = this.props;

        if (!scoreBarType) {

            // ScoreBarNG expects a type
            return null;
        }

        switch (scoreBarType) {
            case ScoreBarTypes.NORMAL:

                // horizontal scorebar with even sections
                // there is a score/developability and displayScore component passed on as children
                return <Normal
                    autoScaleWidth={ autoScaleWidth }
                    classes={{
                        fill: classes.fill,
                        blank: classes.blank,
                        scoreBar: classes.scoreBar,
                        halfScoreFilling: classes.halfScoreFilling,
                        svgContainer: classes.svgContainer
                    }}
                    scoreMin={ scoreMin }
                    scoreMax={ scoreMax }
                    scoreValue={ scoreValue }
                    scoreValueVisible={ scoreValueVisible }
                    displayScore={ displayScore }
                    scoreBarConfig={ scoreBarConfig }
                    scoreName={ scoreName }
                    fill={ false }
                    floor={ floor }
                    languageId={ languageId }
                    developabilityLabel={ developabilityLabel }
                    developabilityScore={ developabilityScore }
                    developabilityDefinition={ developabilityDefinition }
                >
                    <ScoreWithDevelopability
                        classes={{
                            scoreBar: classes.scoreBar,
                            titleWrapper: classes.titleWrapper,
                            scoreName: classes.scoreName,
                            scoreValue: classes.scoreValue
                        }}
                        languageId={ languageId }
                        scoreValue={ scoreValue }
                        scoreValueVisible={ scoreValueVisible }
                        scoreName={ scoreName }
                        displayScore={ displayScore }
                        developabilityLabel={ developabilityLabel }
                        developabilityScore={ developabilityScore }
                        developabilityDefinition={ developabilityDefinition }
                    />
                    { displayScore
                        ? <DisplayScore score={ displayScore } offset={ scoreValue } classes={{ root: classes.displayScore || '' }} />
                        : null
                    }
                </Normal>;

            case ScoreBarTypes.STARS:

                // scorebar that consists of stars that can be half-filled
                return <Stars
                    scoreValue={ scoreValue }
                    scoreDivisionMinimum={ scoreDivisionMinimum }
                    scoreMax={ scoreMax }
                    classes={{
                        active: classes.active,
                        inactive: classes.inactive,
                        root: classes.root,
                        header: classes.header
                    }}
                    scoreLevelDescription={ scoreLevelDescription }
                    label={ label }
                />;

            case ScoreBarTypes.CROISSANT:

                // horizontal scorebar with uneven sections
                // there is a score/developability component passed on as a child
                return <Croissant
                    autoScaleWidth={ autoScaleWidth }
                    classes={{
                        fill: classes.fill,
                        blank: classes.blank,
                        scoreBar: classes.scoreBar,
                        halfScoreFilling: classes.halfScoreFilling,
                        svgContainer: classes.svgContainer
                    }}
                    scoreMin={ scoreMin }
                    scoreMax={ scoreMax }
                    scoreValue={ scoreValue }
                    scoreBarConfig={ scoreBarConfig }
                    fill={ true }
                    languageId={ languageId }
                >
                    <ScoreWithDevelopability
                        classes={{
                            scoreBar: classes.scoreBar,
                            titleWrapper: classes.titleWrapper,
                            scoreName: classes.scoreName,
                            scoreValue: classes.scoreValue
                        }}
                        languageId={ languageId }
                        scoreValue={ scoreValue }
                        scoreValueVisible={ scoreValueVisible }
                        scoreName={ scoreName }
                        displayScore={ displayScore }
                        developabilityLabel={ developabilityLabel }
                        developabilityScore={ developabilityScore }
                        developabilityDefinition={ developabilityDefinition }
                    />
                </Croissant>;

            case ScoreBarTypes.BAR_GRAPH:

                // redesigned scorebar with uneven sections
                return <BarGraph
                    classes={{
                        barGraph: classes.barGraph,
                        displayScore: classes.displayScore,
                        categoryScore: classes.categoryScore
                    }}
                    scoreValue={ scoreValue }
                    scoreName={ scoreName }
                    displayScore={ displayScore }
                    fill={ false }
                    evaluation={ evaluation }
                />;

            case ScoreBarTypes.CUTOFF:

                // horizontal scorebar with optional cutoff section
                return <CutOff
                    classes={{
                        root: classes.root,
                        scoreBar: classes.scoreBar,
                        scoreBarRoot: classes.scoreBarRoot,
                        scoreBarTrack: classes.scoreBarTrack,
                        scoreBarThumb: classes.scoreBarThumb,
                        scoreBarRail: classes.scoreBarRail
                    }}
                    scoreMin={ scoreMin }
                    scoreMax={ scoreMax }
                    scoreValue={ scoreValue }
                    fill={ false }
                    disabled={ disabled }
                    digits={ digits }
                    valueLabelDisplay={ valueLabelDisplay }
                    onChange={ onChange }
                    onChangeCommitted={ onChangeCommitted }
                    cutOff={ cutOff }
                />;

            case ScoreBarTypes.GAUGE:

                return <Gauge
                    scoreMin={ scoreMin }
                    scoreMax={ scoreMax }
                    scoreValue={ scoreValue }
                    initials={ initials }
                    levelRanges={ levelRanges }
                    classes={{
                        root: classes.root,
                        colors: classes.colors
                    }}
                />;

            default:
                return null;
        }
    }
}

ScoreBarNG.propTypes = {
    scoreMin: PropTypes.number,
    scoreMax: PropTypes.number,
    scoreValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    fill: PropTypes.bool,
    floor: PropTypes.bool,
    classes: PropTypes.shape({
        root: PropTypes.string,
        header: PropTypes.string,
        active: PropTypes.string,
        inactive: PropTypes.string,
        svgContainer: PropTypes.string,
        displayScore: PropTypes.string,
        categoryScore: PropTypes.string,
        barGraph: PropTypes.shape({
            root: PropTypes.string,
            fill: PropTypes.string,
            blank: PropTypes.string
        }),
        titleWrapper: PropTypes.string,
        scoreBar: PropTypes.string,
        scoreBarRoot: PropTypes.string,
        scoreBarThumb: PropTypes.string,
        scoreBarTrack: PropTypes.string,
        scoreBarRail: PropTypes.string,
        fill: PropTypes.string,
        blank: PropTypes.string,
        halfScoreFilling: PropTypes.string,
        scoreName: PropTypes.string,
        scoreValue: PropTypes.string,
        colors: PropTypes.object
    }),
    scoreName: PropTypes.string,
    scoreValueVisible: PropTypes.bool,
    config: PropTypes.shape({
        barWidth: PropTypes.number,
        barMargin: PropTypes.number,
        barHeight: PropTypes.number,
        activeBarHeight: PropTypes.number,
        barBorderRadius: PropTypes.number,
        filled: PropTypes.bool
    }),
    displayScore: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    languageId: PropTypes.string,
    developabilityScore: PropTypes.string,
    developabilityLabel: PropTypes.string,
    developabilityDefinition: PropTypes.string,
    evaluation: PropTypes.string,
    label: PropTypes.string,
    autoScaleWidth: PropTypes.bool,
    disabled: PropTypes.bool,
    digits: PropTypes.number,
    valueLabelDisplay: PropTypes.string,
    onChange: PropTypes.func,
    onChangeCommitted: PropTypes.func,
    cutOff: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    initials: PropTypes.string,
    levelRanges: PropTypes.array
};

ScoreBarNG.defaultProps = {
    scoreMin: 1,
    scoreMax: 1,
    fill: false,
    floor: true,
    classes: {
        root: '',
        header: '',
        active: '',
        inactive: '',
        svgContainer: '',
        displayScore: '',
        categoryScore: '',
        barGraph: {
            root: '',
            fill: '',
            blank: ''
        },
        titleWrapper: '',
        scoreBar: '',
        scoreBarRoot: '',
        scoreBarThumb: '',
        scoreBarTrack: '',
        scoreBarRail: '',
        fill: '',
        blank: '',
        halfScoreFilling: '',
        scoreName: '',
        scoreValue: ''
    },
    autoScaleWidth: true,
    scoreName: '',
    scoreValueVisible: true,
    config: {
        barWidth: 36,
        barMargin: 0,
        barHeight: 8,
        activeBarHeight: 11,
        barBorderRadius: 5
    },
    languageId: '',
    developabilityScore: '',
    developabilityLabel: '',
    developabilityDefinition: '',
    disabled: false,
    digits: 2
};
